import React from 'react';
import _ from 'lodash';

import { Line } from './namedEntityRecognitionParser';
import { INamedEntityRecognitionLabel, getColormapForLabels } from './namedEntityRecognitionLabels';
import { useNamedEntityRecognition } from './namedEntityRecognitionAnnotations';
import Combokeys from 'combokeys';

import { INamedEntityRecognitionResponse } from '@scale/shared/types/response/namedentityrecognition';
import { NERTextCanvas } from 'react-scale/components/scalers/tasks/namedEntityRecognition/NERTextCanvas';

import { makeStyles } from 'design-system/styles';
import { depthFirstLabels, mergeLabelAttributes } from '@scale/shared/namedentityrecognition/label';
import Fuse from 'fuse.js';
import NERLineParser from './NERLineParser';
import {
  INamedEntityRecognitionAttribute,
  INamedEntityRecognitionRelationshipDefinition,
  NerAttachment,
  NerAttachmentPlacement,
  NerAttachmentType,
} from '@scale/shared/types/params/namedentityrecognition';
import { useElementSizeCallback } from 'react-scale/hooks/useElementSize';
import { NamedEntityRecognitionSidebar } from './NamedEntityRecognitionSidebar';
import { NamedEntityRecognitionAssignmentJson } from '@scale/shared/assignmentJson/types/NamedEntityRecognitionAssignmentJson';
import AttachmentView from './AttachmentView';
import { NERGlobalAttribute } from '@scale/shared/types/annotation';
import clsx from 'clsx';

interface IProps {
  textDirection?: 'rtl' | 'ltr'; // For Arabic languages
  taskId?: string;
  readOnly: boolean;
  hideLabels?: boolean;
  assignment?: NamedEntityRecognitionAssignmentJson;
  defaultAttributeFields?: Record<string, INamedEntityRecognitionAttribute>;
  labels: INamedEntityRecognitionLabel[];
  relationships?: INamedEntityRecognitionRelationshipDefinition[];
  globalAttributes?: Record<string, NERGlobalAttribute>;
  text: string;
  attachments?: NerAttachment[];
  mergeNewlines?: boolean;
  preserveTrailingPunctuation?: boolean;
  allowOverlappingAnnotations?: boolean;
  defaultFreeSelect?: boolean;
  initialResponse?: INamedEntityRecognitionResponse;
  useProtectedText?: boolean;
  loggedUserId?: string;
  onResponseChange?: (response: INamedEntityRecognitionResponse) => void;
  onViewedAllText?: () => void;
  isReviewMode?: boolean;
  recordLinkClickingInTextAttachment?: {
    innerTextKeywords: string;
    onSubmitDisabled: (val: boolean) => void;
  };
  className?: string;
}

const SCROLL_PADDING = 100;

export const NER_CANVAS_MAX_WIDTH = 1152;

export const NamedEntityRecognition: React.FC<IProps> = props => {
  const {
    labels,
    relationships: relationshipDefinitions,
    globalAttributes: globalAttributesDefinitions,
    defaultAttributeFields,
    onViewedAllText,
    readOnly,
    text,
    initialResponse,
    allowOverlappingAnnotations,
    useProtectedText,
    loggedUserId,
    recordLinkClickingInTextAttachment,
    hideLabels,
    className,
  } = props;

  // amazon hack :) TODO more natively support this feature
  const videoSrc =
    props.attachments && props.attachments[0].type === NerAttachmentType.Video
      ? props.attachments?.[0].content
      : null;

  const videoRef = React.useRef<HTMLVideoElement>(null);

  const setPlayback = React.useCallback(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 1.5;
    }
  }, [videoSrc]);

  const [lines, setLines] = React.useState<Line[] | null>(null);

  const labelColormap = React.useMemo(() => getColormapForLabels(props.labels), [props.labels]);

  const labelAttributeTable = React.useMemo(() => {
    return mergeLabelAttributes(labels);
  }, [labels]);

  const {
    focusedAnnotationIds,
    unFocusAnnotation,
    focusAnnotationById,
    focusAnnotationJump,
    annotations,
    relationships,
    response,
    globalAttributes,
    addAnnotationFromRange,
    updateAnnotationLabelById,
    updateAnnotationAttributeById,
    removeFocusedAnnotation,
    setRelationship,
    removeRelationship,
    handleGlobalAttributeChange,
    undo,
    redo,
    getValidRelationshipsForAnnotationPair,
    getValidRelationshipTargets,
    checkRequiredAttributeExist,
  } = useNamedEntityRecognition({
    readOnly,
    text,
    lines,
    labels,
    relationshipDefinitions,
    initialResponse,
    allowOverlappingAnnotations,
    globalAttributesDefinitions,
  });

  const sortedAnnotations = React.useMemo(
    () => _.sortBy(annotations, [x => x.startLineIdx, x => x.startOffset]),
    [annotations],
  );

  const sortedRelationships = React.useMemo(
    () =>
      _.sortBy(relationships, [
        x => x.topAnnotation.startLineIdx,
        x => x.topAnnotation.startOffset,
        x => x.bottomAnnotation.startLineIdx,
        x => x.bottomAnnotation.startOffset,
      ]),
    [relationships],
  );

  const [hoverredLabelId, setHoverredLabelId] = React.useState<string | null>(null);
  const [showResponse, setShowResponse] = React.useState(false);

  const labelSearchIndex = React.useMemo(() => {
    const searchableLabels: INamedEntityRecognitionLabel[] = [];
    for (const label of depthFirstLabels(labels)) {
      if (!label.children) {
        searchableLabels.push(label);
      }
    }
    return new Fuse(searchableLabels, {
      shouldSort: true,
      ignoreLocation: true,
      ignoreFieldNorm: true,
      useExtendedSearch: true,
      threshold: 0.4,
      keys: ['name', 'display_name'],
    });
  }, [labels]);

  React.useEffect(() => {
    if (props.onResponseChange) {
      props.onResponseChange(response);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  React.useEffect(() => {
    // NextJs
    if (typeof window === 'undefined') {
      return;
    }

    const combokeys = new Combokeys(document.body);

    combokeys.bind(['shift+s'], () => {
      setShowResponse(prev => !prev);
    });

    combokeys.bind(['mod+z'], () => {
      undo();
      return false;
    });

    combokeys.bind(['mod+y', 'shift+mod+z'], () => {
      redo();
      return false;
    });

    combokeys.bind(['esc', 'enter'], () => {
      unFocusAnnotation();
      return false;
    });

    combokeys.bind(['backspace', 'del'], () => {
      removeFocusedAnnotation();
      return false;
    });

    combokeys.bind(['tab'], () => {
      focusAnnotationJump('forward');
      return false;
    });

    combokeys.bind(['shift+tab'], () => {
      focusAnnotationJump('backward');
      return false;
    });

    return () => {
      combokeys.detach();
    };
  });

  const classes = useNERStyles();

  const { ref: canvasSizeCallbackRef, size: canvasSize } = useElementSizeCallback();

  // ======= check link clicking behavior ======= /
  const linkElems: HTMLAnchorElement[] = [];
  if (recordLinkClickingInTextAttachment) {
    const parser = new DOMParser();
    for (const attachment of props.attachments ?? []) {
      if (attachment.type === NerAttachmentType.Text) {
        const htmlContent = parser.parseFromString(attachment.content, 'text/html');
        linkElems.push(...Array.from(htmlContent.getElementsByTagName('a')));
      }
    }
  }
  const linkElemsToClick = linkElems
    .filter(elem =>
      recordLinkClickingInTextAttachment?.innerTextKeywords.includes(`"${elem.innerText}"`),
    )
    .map(elem => elem.href);

  const [linkClickHistory, setLinkClickHistory] = React.useState<{ [link: string]: boolean }>(
    Object.fromEntries(linkElemsToClick.map(elem => [elem, false])),
  );

  const handleLinkClickHistory = (link: string) => {
    if (recordLinkClickingInTextAttachment && Object.keys(linkClickHistory).includes(link)) {
      const newLinkClickHistory = { ...linkClickHistory, [link]: true };
      setLinkClickHistory(newLinkClickHistory);
    }
  };

  React.useEffect(() => {
    if (recordLinkClickingInTextAttachment && !props.isReviewMode) {
      recordLinkClickingInTextAttachment.onSubmitDisabled(
        !Object.values(linkClickHistory).every(elem => elem),
      );
    }
  }, [recordLinkClickingInTextAttachment, linkClickHistory]);

  // ======= end check link clicking behavior /

  if (showResponse) {
    return (
      <div className={clsx(classes.container, className)}>
        <textarea
          style={{ display: 'block', minWidth: 1000, minHeight: 500 }}
          value={JSON.stringify(response, null, 2)}
          readOnly
        />
      </div>
    );
  }

  const handleScroll = (target: HTMLDivElement) => {
    if (target.scrollHeight - target.scrollTop <= target.clientHeight + SCROLL_PADDING) {
      onViewedAllText?.();
    }
  };

  const onCanvasScroll = (e: React.SyntheticEvent<HTMLDivElement>) => {
    handleScroll(e.currentTarget);
  };

  const onCanvasRef = (target: HTMLDivElement | null) => {
    if (target) {
      handleScroll(target);
      canvasSizeCallbackRef(target);
    }
  };

  const [topAttachments, bottomAttachments] = _.partition(
    videoSrc ? [] : props.attachments || [],
    a => a.placement === NerAttachmentPlacement.Top,
  );

  return (
    <div className={classes.container}>
      <NERLineParser
        text={props.text}
        textClassName={classes.text}
        maxWidth={Math.min(canvasSize.width ?? NER_CANVAS_MAX_WIDTH, NER_CANVAS_MAX_WIDTH)}
        mergeNewlines={props.mergeNewlines}
        onLinesChange={setLines}
      />
      <div className={classes.contentArea} onScroll={onCanvasScroll} ref={onCanvasRef}>
        <div className={classes.contentCentered}>
          {topAttachments.length > 0 &&
            topAttachments.map((a: NerAttachment, i: number) => (
              <AttachmentView
                key={`top attachment ${i}`}
                attachment={a}
                useProtectedText={useProtectedText}
                loggedUserId={props.loggedUserId}
                handleLinkClickHistory={handleLinkClickHistory}
                stacked={true}
              />
            ))}
          {lines && (
            <NERTextCanvas
              addAnnotationFromRange={addAnnotationFromRange}
              updateAnnotationLabelById={updateAnnotationLabelById}
              updateAnnotationAttributeById={updateAnnotationAttributeById}
              focusAnnotationById={focusAnnotationById}
              unFocusAnnotation={unFocusAnnotation}
              focusedAnnotationIds={focusedAnnotationIds}
              labels={props.labels}
              labelColormap={labelColormap}
              sortedAnnotations={sortedAnnotations}
              relationshipDefinitions={relationshipDefinitions}
              relationships={sortedRelationships}
              preserveTrailingPunctuation={props.preserveTrailingPunctuation}
              allowOverlappingAnnotations={props.allowOverlappingAnnotations}
              defaultFreeSelect={props.defaultFreeSelect}
              textDirection={
                props.assignment?.featureFlags?.rightAlignText ? 'rtl' : props.textDirection
              }
              lines={lines}
              contentClassName={classes.contentText}
              textClassName={classes.text}
              labelSearchIndex={labelSearchIndex}
              setRelationship={setRelationship}
              removeRelationship={removeRelationship}
              getValidRelationshipsForAnnotationPair={getValidRelationshipsForAnnotationPair}
              getValidRelationshipTargets={getValidRelationshipTargets}
              useProtectedText={useProtectedText}
              loggedUserId={loggedUserId}
              labelAttributeTable={labelAttributeTable}
              removeFocusedAnnotation={removeFocusedAnnotation}
              checkRequiredAttributeExist={checkRequiredAttributeExist}
              defaultAttributeFields={defaultAttributeFields}
              readOnly={readOnly}
            />
          )}
          {bottomAttachments.length > 0 &&
            bottomAttachments.map((a: NerAttachment, i: number) => (
              <AttachmentView
                key={`bottom attachment ${i}`}
                attachment={a}
                useProtectedText={useProtectedText}
                loggedUserId={props.loggedUserId}
                handleLinkClickHistory={handleLinkClickHistory}
                stacked={true}
              />
            ))}
          {videoSrc && (
            <div className={classes.videoContainer}>
              <video
                id="myVideo"
                ref={videoRef}
                onCanPlay={setPlayback}
                src={videoSrc}
                width="90%"
                controls
                muted
              />
            </div>
          )}
        </div>
      </div>
      {!hideLabels && (
        <NamedEntityRecognitionSidebar
          labels={props.labels}
          colorMap={labelColormap}
          hoverredLabelId={hoverredLabelId}
          setHoverredLabelId={setHoverredLabelId}
          onChange={handleGlobalAttributeChange}
          isLabelRequired={false}
          isReadOnly={readOnly}
          attrDefinitions={globalAttributesDefinitions}
          attrSelections={globalAttributes}
        />
      )}
    </div>
  );
};

const useNERStyles = makeStyles({
  text: {
    fontWeight: 400,
    fontSize: '18px',
  },
  contentText: {
    background: '#ffffff',
    boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.1)',
    borderRadius: '8px',
    padding: '24px 30px',
    display: 'flex',
    flexDirection: 'column',
  },
  container: {
    display: 'flex',
    background: '#fafafa',
    flexGrow: 1,
    flexShrink: 1,
    color: '#000',
    overflow: 'scroll',
  },
  contentArea: {
    display: 'flex',
    justifyContent: 'center',
    flex: 1,
    height: 'max-content',
    padding: '30px',
  },
  contentCentered: {
    maxWidth: NER_CANVAS_MAX_WIDTH,
  },
  videoContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '100px',
    minWidth: '600px',
  },
});
